// src/components/validationNotes/PageMoisPrimaire.jsx
// ============================================================================
// Le mois d'une classe du primaire, vu par la direction
// (/administration/notes/saisies/primaire/:classeId/:moisId)
// ============================================================================
//
// Au primaire, le titulaire saisit et soumet ses notes PAR MATIÈRE et par
// mois (une feuille = classe × matière × mois, notée sur 10 au demi-point) ;
// la direction valide LE MOIS DE LA CLASSE en un geste : toutes les matières
// soumises de ce mois partent ensemble au carnet (décision D2 de Daniel,
// 7 octobre 2026). Valider matière par matière republierait le mois aux
// familles, avec une autre moyenne et un autre rang, à chaque matière.
//
// Une page, plusieurs entrées : la file « Saisies des enseignants » du carnet,
// l'onglet « Notes » de la page de l'enseignant dans le suivi, et la cloche
// (une notification par classe et par mois tant qu'elle n'est pas lue).
//
// Ce qu'elle montre (maquette validée, DirectionMois) : chaque matière déclarée
// pour la classe, son coefficient (lu, jamais saisi ; 0 = hors moyenne), son
// état, les élèves notés, la moyenne de la classe, et ce que le report fera au
// carnet (notes nouvelles, remplacées, conflits) ; un avertissement si des
// matières ne sont pas encore soumises ; ce que la validation fera aux
// familles. Les valeurs d'un brouillon ne sortent jamais du serveur.
//
// Ce qu'elle laisse faire :
//   - « Valider le mois (N matières) » : les N feuilles soumises, avec la
//     version affichée de chacune, dans UNE transaction côté serveur (tout ou
//     rien), report SANS verrou. Sont laissées de côté, et dites : celles que
//     l'employé a saisies ou soumises (un autre membre de la direction les
//     validera) et celles d'une matière qui n'est plus déclarée pour la
//     classe (le serveur les refuserait, et tout le mois avec elles). Une
//     matière soumise plus tard se valide par un nouvel appel, depuis la même
//     page ; une matière rectifiée puis resoumise aussi ;
//   - ouvrir la feuille d'une matière (PageFeuilleNotes) pour la lire en
//     détail, la renvoyer à l'enseignant avec un motif, refaire le report
//     d'élèves restés en conflit ou décider d'une rectification ;
//   - ouvrir le carnet du mois, dont l'enregistrement par le secrétariat
//     verrouille, calcule moyenne et rang, et publie aux familles.
//
// Droits : les clés du primaire seulement (NOTES.VALIDATION_PRIMAIRE.*), sans
// repli sur celles du collège ni sur une porte de module ; jamais l'auteur
// d'une matière pour cette matière (`est_auteur`, VALIDATEUR_AUTEUR côté
// serveur).
//
// Consignes de Daniel (maquette DirectionMois validée) : titre en gras
// prononcé, retour en bouton bordé, badges à l'arrondi d'un bouton, tiret
// court dans tout texte affiché.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, AlertTriangle, ClipboardCheck, FileText, Inbox, Info, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import EtatVide from '../ui/EtatVide';
import { Toast } from '../ui/Toast';
import LongToast, { shouldUseLongToast } from '../ui/LongToast';
import ToastPortal from '../ToastPortal';
import ConfirmDialog from '../ConfirmDialog';
import { useFeatures } from '../../contexts/FeaturesContext';
import { usePermissions } from '../../hooks/usePermissions';
import { estVrai, libelleDe, nombre, nomEleve, pluriel } from '../suiviEnseignants/formatSuivi';
import { validationNotes } from './apiValidationNotes';
import { CLES_VALIDATION_PRIMAIRE, droitsDuNiveau, useAccesValidationNotes } from './droitsValidationNotes';
import {
  BadgeStatutFeuille, BoutonRetour, baremeDe, ecrireNote, intituleMois, libelleMotif, libelleTrimestre, lireNote,
  nomEmploye
} from './presentationNotes';

// Les refus qui se lisent dans la fenêtre, sans la fermer : l'auteur d'une
// matière ne peut pas valider ce mois (un autre membre de la direction le
// fera), des notes en cours de modification ailleurs (un instant). Les autres
// 403 / 404 / 409 disent que l'écran est en retard (version changée, feuille
// retirée, droit retiré) : on ferme et on relit.
const REFUS_A_LIRE_SUR_PLACE = new Set(['VALIDATEUR_AUTEUR', 'NOTES_OCCUPEES']);
const ecranEnRetard = (erreur) => erreur?.code === 'VERSION_REQUISE'
  || (!REFUS_A_LIRE_SUR_PLACE.has(erreur?.code) && [403, 404, 409].includes(erreur?.statut));

// Une matière qui n'est pas (ou plus) chez la direction : elle viendra au
// carnet à sa propre validation.
const PAS_ENCORE_SOUMISES = ['a_saisir', 'brouillon', 'renvoyee'];
const MOTS_ETAT = { a_saisir: 'à saisir', brouillon: 'brouillon', renvoyee: 'renvoyée' };

const BOUTON_ENTETE = 'h-10 gap-2 px-4';
const BOUTON_PRINCIPAL = 'bg-[#246990] text-white hover:bg-[#1f5a7a]';
const LIEN_BOUTON = 'inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-4 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/30';
const LIEN_LIGNE = 'inline-flex h-9 w-[150px] items-center justify-center gap-2 whitespace-nowrap rounded-md border border-gray-200 bg-white px-3 text-[13px] font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/30';

// Les colonnes du tableau des matières, en grille à partir de 1280 px ; en
// dessous (tablette), chaque matière se lit en carte.
const COLONNES = 'xl:grid-cols-[minmax(0,1.3fr)_96px_176px_110px_90px_minmax(0,2fr)_150px]';

const statutDe = (matiere) => matiere?.feuille?.statut || 'a_saisir';

// Un trimestre que le carnet sait écrire (`notes.trimestre`) : 1, 2 ou 3. Le
// serveur rend le trimestre du mois en chaîne, « null » compris.
const TRIMESTRES_VALIDES = ['1', '2', '3'];
const trimestreValide = (trimestre) => TRIMESTRES_VALIDES.includes(String(trimestre ?? ''));

/** « de Lecture », « d'Anglais », « d'Histoire » : l'élision devant une voyelle ou un h. */
const de = (mot) => (/^[aeiouyhàâäéèêëîïôöùûüœæ]/i.test(String(mot || '').trim()) ? `d'${mot}` : `de ${mot}`);

// Une matière soumise de nouveau après une rectification : ses notes ont déjà
// été reportées une fois. Ce que la validation remplace au carnet, ce sont
// alors (pour l'essentiel) les notes que cette feuille y avait mises, pas des
// notes « de l'école ».
const dejaValideeUneFois = (matiere) => Boolean(matiere?.feuille?.validee_le);

// Une matière dont la feuille existe mais qui n'est plus déclarée pour la
// classe (`dans_la_classe: false`) : le serveur refuserait de la reporter
// (jamais une matière hors de `matiere_classes`), et tout le mois avec elle.
const horsClasse = (matiere) => matiere?.dans_la_classe === false;

/** La prévision du report d'une matière, en nombres sûrs. */
function previsionDe(matiere) {
  const p = matiere?.prevision || {};
  return {
    lue: Boolean(matiere?.prevision),
    inserees: nombre(p.inserees),
    remplacees: nombre(p.remplacees),
    identiques: nombre(p.identiques),
    retirees: nombre(p.retirees),
    conflits: Array.isArray(p.conflits) ? p.conflits : []
  };
}

/** Le nom d'un élève d'un conflit (`eleve` chaîne ou objet), sinon son numéro. */
const nomDuConflit = (conflit) => libelleDe(conflit?.eleve) || nomEleve(conflit?.eleve) || `élève n° ${conflit?.eleve_id ?? '?'}`;

/** « verrouillée au carnet (KIMBEMBE Prisca) », « KIMBEMBE Prisca, MBEMBA Rodrigue et 2 autres ». */
function detailConflits(conflits) {
  const noms = conflits.slice(0, 3).map(nomDuConflit);
  const reste = conflits.length - noms.length;
  const liste = reste > 0 ? `${noms.join(', ')} et ${pluriel(reste, 'autre', 'autres')}` : noms.join(', ');
  const motifs = new Set(conflits.map((c) => c?.motif));
  if (motifs.size === 1) return `${libelleMotif(conflits[0]?.motif).toLowerCase()} (${liste})`;
  return liste;
}

/** Ce que le report fera (ou a fait) au carnet pour une matière : `{ texte, alerte }`. */
function phraseCarnet(matiere) {
  const statut = statutDe(matiere);
  if (horsClasse(matiere) && statut === 'soumise') {
    return { texte: 'Plus dans les matières de la classe : ne peut pas être validée, à renvoyer', alerte: true };
  }
  if (statut === 'a_saisir') {
    // Ce que le carnet porte déjà pour cette matière ce mois-ci (saisi par
    // l'école), que l'enseignant n'a pas encore commencé.
    const lignes = nombre(matiere?.carnet?.lignes);
    const verrouillees = nombre(matiere?.carnet?.verrouillees);
    if (lignes > 0) {
      return {
        texte: `${pluriel(lignes, "note de l'école", "notes de l'école")} au carnet`
          + (verrouillees > 0 ? `, ${pluriel(verrouillees, 'verrouillée', 'verrouillées')}` : ''),
        alerte: false
      };
    }
    return { texte: '-', alerte: false };
  }
  if (statut === 'brouillon') return { texte: "Les notes d'un brouillon ne sont pas montrées", alerte: false };
  if (statut === 'renvoyee') return { texte: "Renvoyée à l'enseignant : rien n'est reporté", alerte: false };
  if (statut === 'rectification_autorisee') return { texte: "Rouverte à l'enseignant pour une rectification", alerte: false };

  const p = previsionDe(matiere);
  if (statut === 'validee' || statut === 'rectification_demandee') {
    if (p.conflits.length > 0) {
      return {
        texte: `${pluriel(p.conflits.length, 'élève non reporté', 'élèves non reportés')} : ${detailConflits(p.conflits)}`,
        alerte: true
      };
    }
    return { texte: 'Reportée au carnet', alerte: false };
  }

  // Soumise : ce que fera la validation du mois.
  if (!p.lue) return { texte: 'Détail sur la feuille', alerte: false };
  const seulementNeuves = p.inserees > 0 && !p.remplacees && !p.identiques && !p.retirees && !p.conflits.length;
  if (seulementNeuves) return { texte: `Vide : ${pluriel(p.inserees, 'note nouvelle', 'notes nouvelles')}`, alerte: false };
  const morceaux = [
    p.conflits.length > 0 ? `${pluriel(p.conflits.length, 'conflit', 'conflits')} : ${detailConflits(p.conflits)}` : '',
    p.remplacees > 0
      ? (dejaValideeUneFois(matiere)
        ? pluriel(p.remplacees, 'note du carnet sera remplacée (rectification)', 'notes du carnet seront remplacées (rectification)')
        : pluriel(p.remplacees, "note de l'école sera remplacée", "notes de l'école seront remplacées"))
      : '',
    p.retirees > 0 ? pluriel(p.retirees, 'note sera effacée', 'notes seront effacées') : '',
    p.inserees > 0 ? pluriel(p.inserees, 'note nouvelle', 'notes nouvelles') : '',
    p.identiques > 0 ? `${p.identiques} déjà au carnet` : ''
  ].filter(Boolean);
  if (!morceaux.length) return { texte: 'Rien à reporter', alerte: false };
  return { texte: morceaux.join(' · '), alerte: p.conflits.length > 0 || p.remplacees > 0 || p.retirees > 0 };
}

/** « 35 / 35 », « 34 + 1 absent » : les élèves notés d'une feuille, jamais ses valeurs. */
function notesSaisies(matiere, nbEleves) {
  const feuille = matiere?.feuille;
  if (!feuille || feuille.notes === null || feuille.notes === undefined) return '-';
  const notes = nombre(feuille.notes);
  const absents = nombre(feuille.absents);
  if (absents > 0) return `${notes} + ${pluriel(absents, 'absent')}`;
  const sur = feuille.eleves !== undefined && feuille.eleves !== null ? nombre(feuille.eleves) : nbEleves;
  return sur ? `${notes} / ${sur}` : String(notes);
}

/** « 2 », « 0 · hors moy. », « - » (non renseigné) : le coefficient lu au paramétrage. */
function coefficientDe(matiere) {
  const coef = lireNote(matiere?.coefficient);
  const hors = estVrai(matiere?.hors_moyenne) || coef === 0;
  if (coef === null) return { texte: hors ? 'hors moy.' : '-', hors };
  return { texte: hors ? `${ecrireNote(coef)} · hors moy.` : ecrireNote(coef), hors };
}

/** « Histoire-Géographie (brouillon) et Calcul écrit (à saisir) ». */
function enumerer(elements, rendre) {
  const morceaux = elements.map(rendre);
  if (morceaux.length <= 1) return morceaux;
  return morceaux.flatMap((m, i) => {
    if (i === 0) return [m];
    return [i === morceaux.length - 1 ? ' et ' : ', ', m];
  });
}

/** Un compteur de la page (maquette : valeur en avant, sous-titre de sens). */
const Compteur = ({ libelle, valeur, sur = null, note = null, tonNote = 'neutre', tonValeur = null, petit = false }) => {
  const encres = { neutre: 'text-gray-500', attention: 'text-amber-700', bien: 'text-emerald-700' };
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3.5">
      <p className="text-xs text-gray-500">{libelle}</p>
      <p className={`${petit ? 'mt-2 text-lg' : 'mt-1 text-2xl'} font-semibold leading-tight ${tonValeur ? encres[tonValeur] : 'text-gray-900'}`}>
        {valeur}
        {sur !== null && <span className="text-sm font-medium text-gray-500"> / {sur}</span>}
      </p>
      {note && <p className={`mt-0.5 text-xs ${encres[tonNote] || encres.neutre}`}>{note}</p>}
    </div>
  );
};

/** Une cellule du tableau : son libellé n'apparaît qu'en carte (sous 1280 px). */
const Cellule = ({ libelle, className = '', children }) => (
  <div className={`flex min-w-0 gap-2 xl:block ${className}`}>
    <span className="w-24 shrink-0 text-gray-500 xl:hidden">{libelle}</span>
    <div className="min-w-0">{children}</div>
  </div>
);

const PageMoisPrimaire = () => {
  const { classeId, moisId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { estActive } = useFeatures();
  const { hasPermission } = usePermissions();
  const { aLaCle } = useAccesValidationNotes();

  const [donnees, setDonnees] = useState(null);
  const [chargement, setChargement] = useState(false);
  // `{ message, statut }` : le statut dit si « Réessayer » a un sens (pas pour
  // un mois introuvable ni un droit refusé).
  const [erreur, setErreur] = useState(null);
  const [toast, setToast] = useState(null);
  const [dialogue, setDialogue] = useState(false);
  const [erreurDialogue, setErreurDialogue] = useState(null);
  const [envoi, setEnvoi] = useState(false);

  // Seule la réponse de la DERNIÈRE demande s'affiche (relecture après la
  // validation pendant qu'une autre lecture est en route).
  const demandeEnCours = useRef(0);
  // Verrou immédiat : `envoi` ne désactive le bouton qu'au rendu suivant ; un
  // second clic arrivé avant enverrait une seconde validation, refusée (409).
  const actionEnCours = useRef(false);

  const charger = useCallback(async () => {
    const numero = ++demandeEnCours.current;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await validationNotes.mois(classeId, moisId);
      if (numero === demandeEnCours.current) setDonnees(reponse.data);
    } catch (e) {
      if (numero === demandeEnCours.current) setErreur({ message: e.message, statut: e.statut || null });
    } finally {
      if (numero === demandeEnCours.current) setChargement(false);
    }
  }, [classeId, moisId]);

  // Un autre mois : on repart de zéro, sans montrer un instant le précédent.
  useEffect(() => { setDonnees(null); }, [classeId, moisId]);
  useEffect(() => { charger(); }, [charger]);

  // ---------------------------------------------------------------------------
  // Ce que dit le mois
  // ---------------------------------------------------------------------------

  const matieres = useMemo(() => (Array.isArray(donnees?.matieres) ? donnees.matieres : []), [donnees]);
  const soumises = useMemo(() => matieres.filter((m) => statutDe(m) === 'soumise' && m.feuille?.id), [matieres]);
  // Ce que « Valider le mois » envoie : les matières soumises, sauf celles que
  // cet employé a saisies ou soumises (un autre membre de la direction les
  // validera : le serveur refuserait tout le mois, VALIDATEUR_AUTEUR) et
  // celles qui ne sont plus déclarées pour la classe (refusées aussi).
  const aValider = useMemo(
    () => soumises.filter((m) => !estVrai(m.feuille?.est_auteur) && !horsClasse(m)),
    [soumises]
  );
  const auteurDe = soumises.filter((m) => estVrai(m.feuille?.est_auteur) && !horsClasse(m));
  const soumisesHorsClasse = soumises.filter(horsClasse);
  const pasEncore = useMemo(
    () => matieres.filter((m) => PAS_ENCORE_SOUMISES.includes(statutDe(m)) && !horsClasse(m)),
    [matieres]
  );
  const validees = matieres.filter((m) => statutDe(m) === 'validee').length;
  const matieresDeLaClasse = matieres.filter((m) => !horsClasse(m)).length;

  // Ce que fera la validation du mois : la somme des prévisions des seules
  // matières qu'elle envoie (celles qui partent au carnet).
  const prevu = useMemo(() => {
    const r = {
      lue: false, inserees: 0, remplacees: 0, identiques: 0, retirees: 0, absents: 0,
      conflits: [], remplacements: []
    };
    aValider.forEach((m) => {
      const p = previsionDe(m);
      r.lue = r.lue || p.lue;
      r.inserees += p.inserees;
      r.remplacees += p.remplacees;
      r.identiques += p.identiques;
      r.retirees += p.retirees;
      r.absents += nombre(m.feuille?.absents);
      p.conflits.forEach((c) => r.conflits.push({ ...c, matiere: m.nom }));
      if (p.remplacees > 0) {
        r.remplacements.push({ matiere: m.nom, nombre: p.remplacees, rectification: dejaValideeUneFois(m) });
      }
    });
    return r;
  }, [aValider]);

  const parentsOuverts = estActive('module.parents');
  const carnetOuvert = hasPermission('CARNET_EVALUATION') && estActive('module.evaluations');
  // Même composition que l'entrée du menu (SidebarNav) : la porte, la clé fine
  // ET la licence du suivi - sans elle, le lien mènerait à « module non inclus ».
  const suiviOuvert = hasPermission('SUIVI_ENSEIGNANTS') && aLaCle('SUIVI_ENSEIGNANTS.VUE_GLOBALE.VIEW')
    && estActive('module.suivi_enseignants');

  // ---------------------------------------------------------------------------
  // Retour
  // ---------------------------------------------------------------------------

  // À la file du carnet ou à la page de l'enseignant telles qu'on les a
  // laissées (filtres dans leur adresse) si l'on en vient : on recule dans
  // l'historique. Arrivée par la cloche ou par un lien : vers l'entrée que la
  // personne peut ouvrir - le carnet d'abord, sinon la page du titulaire dans
  // le suivi ; ni l'un ni l'autre, l'accueil.
  const depuis = location.state?.depuis;
  const titulaireId = donnees?.titulaire && typeof donnees.titulaire === 'object' ? donnees.titulaire.id : null;
  const retour = (() => {
    if (depuis === 'suivi' && location.state?.employeId) {
      return { libelle: "Page de l'enseignant", adresse: `/administration/enseignants/${location.state.employeId}?onglet=notes` };
    }
    if (depuis === 'carnet' || carnetOuvert) {
      return { libelle: 'Saisies des enseignants', adresse: '/administration/notes?onglet=saisies' };
    }
    if (suiviOuvert && titulaireId) {
      return { libelle: "Page de l'enseignant", adresse: `/administration/enseignants/${titulaireId}?onglet=notes` };
    }
    return { libelle: 'Accueil', adresse: '/administration' };
  })();
  const revenir = (e) => {
    if (depuis) {
      e.preventDefault();
      navigate(-1);
    }
  };
  const boutonRetour = <BoutonRetour to={retour.adresse} onClick={revenir}>{retour.libelle}</BoutonRetour>;

  // ---------------------------------------------------------------------------
  // Valider le mois
  // ---------------------------------------------------------------------------

  const ouvrir = () => {
    setErreurDialogue(null);
    setDialogue(true);
  };

  const fermer = () => {
    if (envoi) return;
    setDialogue(false);
    setErreurDialogue(null);
  };

  const messageReussite = (data) => {
    const nbValidees = Array.isArray(data?.validees) ? data.validees.length : aValider.length;
    const rapport = data?.rapport || {};
    const ecrites = nombre(rapport.inserees) + nombre(rapport.mis_a_jour);
    const effacees = nombre(rapport.retirees);
    const conflitsRestes = Array.isArray(data?.conflits)
      ? data.conflits.length
      : (Array.isArray(rapport.conflits) ? rapport.conflits.length : nombre(rapport.conflits));
    return `Mois validé : ${pluriel(nbValidees, 'matière', 'matières')}, ${pluriel(ecrites, 'note écrite', 'notes écrites')} au carnet`
      + (effacees > 0 ? `, ${pluriel(effacees, 'note effacée', 'notes effacées')}` : '')
      + (conflitsRestes > 0
        ? `, ${pluriel(conflitsRestes, 'élève non reporté', 'élèves non reportés')} (conflit avec le carnet, détaillé sur la feuille de la matière).`
        : '.')
      + ' Le carnet du mois reste à enregistrer : c\'est ce qui verrouille les notes'
      + (parentsOuverts ? ' et publie le mois aux familles.' : '.');
  };

  const valider = async () => {
    if (actionEnCours.current || aValider.length === 0) return;
    actionEnCours.current = true;
    setEnvoi(true);
    setErreurDialogue(null);
    try {
      const reponse = await validationNotes.validerMois(
        classeId,
        moisId,
        aValider.map((m) => ({ id: m.feuille.id, version: m.feuille.version }))
      );
      setDialogue(false);
      setToast({ message: messageReussite(reponse?.data), type: 'success' });
      // Le serveur rend la page du mois relue dans la même transaction que la
      // validation : on la pose telle quelle. Une forme inattendue fait relire.
      const page = reponse?.data?.page;
      if (page && Array.isArray(page.matieres)) {
        // Une lecture partie avant serait périmée : elle ne s'affichera pas
        // (numéro dépassé), et ne doit pas laisser la page « en chargement ».
        demandeEnCours.current += 1;
        setDonnees(page);
        setErreur(null);
        setChargement(false);
      } else {
        await charger();
      }
    } catch (e) {
      if (ecranEnRetard(e)) {
        setDialogue(false);
        setToast({ message: e.message, type: 'error' });
        await charger();
      } else {
        setErreurDialogue(e.message);
        // Sans réponse du serveur (connexion coupée), on ne sait pas si la
        // validation est passée : la page se relit derrière la fenêtre, et un
        // second envoi sur une matière déjà validée serait refusé (version).
        if (!e?.statut) charger();
      }
    } finally {
      actionEnCours.current = false;
      setEnvoi(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Rendu
  // ---------------------------------------------------------------------------

  if (!donnees) {
    return (
      <div className="w-full p-6">
        {boutonRetour}
        {erreur ? (
          <div role="alert" className="mt-5 flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="min-w-0 flex-1">{erreur.message}</span>
            {erreur.statut !== 404 && erreur.statut !== 403 && erreur.statut !== 400 && (
              <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
            )}
          </div>
        ) : (
          <div role="status" className="mt-5 flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
            <Loader2 className="h-4 w-4 animate-spin text-[#246990]" />
            Chargement du mois…
          </div>
        )}
      </div>
    );
  }

  const classe = donnees.classe || {};
  const mois = donnees.mois || {};
  const nomClasse = libelleDe(classe) || `Classe ${classeId}`;
  const nomMois = libelleDe(mois) || 'ce mois';
  const titulaire = nomEmploye(donnees.titulaire);
  const nbEleves = Array.isArray(donnees.eleves)
    ? donnees.eleves.length
    : nombre(donnees.eleves ?? classe.eleves);
  const bareme = baremeDe(donnees, true);
  const droits = droitsDuNiveau(donnees.droits, 'primaire');
  const totaux = donnees.totaux || {};
  const publication = donnees.publication || {};
  const publie = estVrai(publication.publie);
  const moisOuvert = mois.ouvert === undefined || mois.ouvert === null ? true : estVrai(mois.ouvert);
  // Un mois fermé l'est pour l'une de deux raisons (`ouvert` du serveur :
  // commencé ET d'un trimestre 1, 2 ou 3) ; on dit laquelle, sans calculer de
  // date ici.
  const trimestreDuMois = trimestreValide(mois.trimestre) ? String(mois.trimestre) : null;

  // Les compteurs : calculés sur la liste des matières (le bouton et le
  // tableau parlent des mêmes), les totaux du serveur à défaut de prévision.
  const aReporter = prevu.lue ? prevu.inserees + prevu.remplacees : nombre(totaux.notes_a_reporter);
  const conflitsPrevus = prevu.lue ? prevu.conflits.length : nombre(totaux.conflits);
  const verrouillees = prevu.conflits.filter((c) => c?.motif === 'verrouillee');
  const autresConflits = prevu.conflits.length - verrouillees.length;
  // Les élèves dont le mois, publié aujourd'hui, sera retiré aux familles par
  // la validation (le serveur les nomme) ; à défaut, une estimation : le mois
  // est publié et la validation ajoute des lignes non verrouillées.
  const depublies = Array.isArray(publication.depublications) ? publication.depublications : null;
  const depublication = parentsOuverts && (depublies ? depublies.length > 0 : (publie && prevu.inserees > 0));

  const peutValider = droits.valider && moisOuvert && aValider.length > 0;
  const raisonSansValidation = (() => {
    if (!droits.valider) {
      return `Vous pouvez lire ce mois, mais pas le valider : il faut, dans le module Notes, la permission ${CLES_VALIDATION_PRIMAIRE.VALIDER}.`;
    }
    if (soumises.length > 0 && aValider.length === 0 && auteurDe.length > 0) {
      return `Vous avez saisi ou soumis ${auteurDe.map((m) => m.nom).join(', ')} : un autre membre de la direction doit ${auteurDe.length > 1 ? 'les' : 'la'} valider.`;
    }
    if (soumises.length > 0 && aValider.length === 0) {
      return "Les matières soumises ne sont plus déclarées pour la classe : elles ne peuvent pas être validées (voir plus bas).";
    }
    if (soumises.length === 0) {
      return pasEncore.length > 0
        ? "Aucune matière n'est soumise pour l'instant : rien à valider. Les matières arrivent ici à mesure que l'enseignant les soumet."
        : "Aucune matière n'attend de validation ce mois-ci.";
    }
    return null;
  })();

  const libelleBouton = aValider.length > 0
    ? `Valider le mois (${pluriel(aValider.length, 'matière', 'matières')})`
    : 'Valider le mois';
  // Des matières soumises que cette validation laisse de côté, et pourquoi -
  // seulement à qui peut valider : « votre validation » ne parle qu'à lui.
  const laisseesDeCote = droits.valider && aValider.length > 0 ? auteurDe : [];

  return (
    <div className="w-full space-y-4 p-6">
      {boutonRetour}

      {erreur && (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">Le mois n'a pas pu être rafraîchi : {erreur.message}</span>
          <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
        </div>
      )}

      {/* En-tête : la classe et le mois, le contexte, les deux gestes */}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs text-gray-500">Carnet d'évaluations › Saisies des enseignants › Primaire</p>
          <h1 className="mt-1 text-2xl font-bold leading-tight text-[#246990]">{intituleMois(classe, mois)}</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            {[
              trimestreDuMois ? libelleTrimestre(trimestreDuMois) : '',
              titulaire ? `titulaire : ${titulaire}` : 'aucun titulaire',
              pluriel(nbEleves, 'élève'),
              `notes sur ${ecrireNote(bareme.max)}`
            ].filter(Boolean).join(' · ')}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {carnetOuvert && (
            // Le carnet d'évaluations s'ouvre sur son onglet « Primaire ». Il ne
            // sait pas encore présélectionner une classe et un mois reçus dans
            // l'adresse (SaisieNotesPrimaire ne lit aucun paramètre) : le libellé
            // ne le promet donc pas (intégration N3).
            <Link
              to="/administration/notes"
              className={LIEN_BOUTON}
              aria-label={`Ouvrir le carnet du primaire, pour y enregistrer ${nomClasse}, ${nomMois}`}
            >
              <FileText className="h-4 w-4" aria-hidden="true" />
              Ouvrir le carnet du primaire
            </Link>
          )}
          {droits.valider && (
            <Button
              size="default"
              className={`${BOUTON_ENTETE} ${BOUTON_PRINCIPAL}`}
              disabled={!peutValider || envoi || chargement}
              onClick={ouvrir}
            >
              <ClipboardCheck className="h-4 w-4" />
              {libelleBouton}
            </Button>
          )}
        </div>
      </header>

      {raisonSansValidation && (
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-700">{raisonSansValidation}</p>
      )}

      {!moisOuvert && (
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-[13px] text-gray-700">
          {trimestreDuMois
            ? "Ce mois n'a pas encore commencé : aucune note ne peut y être saisie ni validée."
            : "Ce mois n'est rattaché à aucun trimestre (1, 2 ou 3) dans le calendrier scolaire : ses notes ne peuvent pas être validées tant que le calendrier n'est pas corrigé."}
        </p>
      )}

      {/* Les compteurs */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="Matières soumises"
          valeur={soumises.length - soumisesHorsClasse.length}
          sur={matieresDeLaClasse}
          tonNote={pasEncore.length > 0 ? 'attention' : (validees > 0 ? 'bien' : 'neutre')}
          note={[
            pasEncore.length > 0 ? pluriel(pasEncore.length, 'pas encore soumise', 'pas encore soumises') : '',
            validees > 0 ? pluriel(validees, 'déjà validée', 'déjà validées') : ''
          ].filter(Boolean).join(' · ') || null}
        />
        <Compteur
          libelle="Notes à reporter"
          valeur={aReporter}
          note={prevu.absents > 0
            ? `et ${pluriel(prevu.absents, 'élève absent', 'élèves absents')}, sans note`
            : (prevu.identiques > 0 ? `${prevu.identiques} déjà au carnet` : null)}
        />
        <Compteur
          libelle="Conflits prévus"
          valeur={conflitsPrevus}
          tonValeur={conflitsPrevus > 0 ? 'attention' : null}
          note={verrouillees.length > 0
            ? pluriel(verrouillees.length, 'ligne verrouillée au carnet', 'lignes verrouillées au carnet')
            : (conflitsPrevus > 0 ? 'détaillés sur la feuille de la matière' : 'aucun')}
        />
        {parentsOuverts ? (
          <Compteur
            libelle="Familles"
            petit
            valeur={publie ? 'Publié' : 'Pas encore publié'}
            tonNote={depublication ? 'attention' : 'neutre'}
            note={depublication
              ? "sera retiré jusqu'au prochain enregistrement du carnet"
              : (publie ? 'notes du mois verrouillées au carnet' : "à l'enregistrement du carnet")}
          />
        ) : (
          <Compteur
            libelle="Verrouillées au carnet"
            valeur={nombre(publication.lignes_verrouillees)}
            note="verrouillées par l'enregistrement du carnet"
          />
        )}
      </div>

      {/* Des matières soumises que cette validation laisse de côté */}
      {laisseesDeCote.length > 0 && (
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <p>
            Vous avez saisi ou soumis{' '}
            {enumerer(laisseesDeCote, (m) => <span key={m.matiere_id} className="font-semibold">{m.nom}</span>)}
            {' '}: {laisseesDeCote.length > 1 ? 'elles ne sont pas comprises' : "elle n'est pas comprise"} dans votre
            validation du mois, un autre membre de la direction {laisseesDeCote.length > 1 ? 'les' : 'la'} validera.
          </p>
        </div>
      )}
      {soumisesHorsClasse.length > 0 && (
        <div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[13px] leading-relaxed text-red-800">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
          <p>
            {enumerer(soumisesHorsClasse, (m) => <span key={m.matiere_id} className="font-semibold">{m.nom}</span>)}
            {soumisesHorsClasse.length > 1
              ? " ne sont plus déclarées pour la classe cette année : elles ne peuvent pas être reportées au carnet. Renvoyez-les à l'enseignant depuis leur feuille, ou déclarez-les de nouveau dans Classes et matières."
              : " n'est plus déclarée pour la classe cette année : elle ne peut pas être reportée au carnet. Renvoyez-la à l'enseignant depuis sa feuille, ou déclarez-la de nouveau dans Classes et matières."}
          </p>
        </div>
      )}

      {/* Des matières pas encore soumises : on peut valider ou attendre (dit à
          qui peut valider ; le compteur le dit à tous) */}
      {droits.valider && aValider.length > 0 && pasEncore.length > 0 && (
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <p>
            {enumerer(pasEncore, (m) => (
              <React.Fragment key={m.matiere_id}>
                <span className="font-semibold">{m.nom}</span> ({MOTS_ETAT[statutDe(m)]})
              </React.Fragment>
            ))}
            {pasEncore.length > 1 ? ' ne sont pas encore soumises.' : " n'est pas encore soumise."}
            {' '}Si vous validez maintenant, {pasEncore.length > 1 ? 'elles viendront' : 'elle viendra'} au carnet à
            {pasEncore.length > 1 ? ' leur' : ' sa'} propre validation
            {parentsOuverts
              ? ', et le mois sera republié aux familles avec une autre moyenne.'
              : ', et la moyenne du mois sera recalculée au prochain enregistrement du carnet.'}
            {' '}Vous pouvez aussi attendre.
          </p>
        </div>
      )}

      {depublication && (
        <div className="flex gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-[13px] leading-relaxed text-amber-900">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" aria-hidden="true" />
          <p>
            Espace famille : ce mois est déjà publié
            {depublies ? ` pour ${pluriel(depublies.length, 'élève', 'élèves')} qui ${depublies.length > 1 ? 'recevront' : 'recevra'} une note nouvelle` : ''}.
            Les notes ajoutées au carnet, non verrouillées, le retirent à {depublies && depublies.length === 1 ? 'sa famille' : 'leurs familles'} jusqu'au
            prochain enregistrement du carnet par le secrétariat (un mois n'est montré que lorsque toutes ses notes sont
            verrouillées).
            {depublies && depublies.length > 0 && (
              <span className="mt-1 block text-xs">
                {depublies.slice(0, 8).map(nomDuConflit).join(', ')}
                {depublies.length > 8 ? ` et ${pluriel(depublies.length - 8, 'autre', 'autres')}` : ''}
              </span>
            )}
          </p>
        </div>
      )}

      {/* Les matières */}
      <section className="rounded-lg border border-gray-200 bg-white">
        {matieres.length === 0 ? (
          <div className="p-4">
            <EtatVide
              compact
              icone={Inbox}
              titre="Aucune matière déclarée pour cette classe"
              description="Les matières du primaire se déclarent pour la classe et l'année dans Classes et matières : sans elles, l'enseignant n'a rien à saisir."
            />
          </div>
        ) : (
          <>
            <div className={`hidden gap-3 border-b border-gray-200 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wide text-gray-500 xl:grid ${COLONNES}`}>
              <span>Matière</span>
              <span>Coef.</span>
              <span>État</span>
              <span>Notés</span>
              <span>Moyenne</span>
              <span>Au carnet</span>
              <span className="sr-only">Feuille</span>
            </div>
            <ul className="divide-y divide-gray-100">
              {matieres.map((m) => {
                const statut = statutDe(m);
                const feuille = m.feuille || null;
                const coef = coefficientDe(m);
                const carnet = phraseCarnet(m);
                const moyenne = feuille && statut !== 'brouillon' ? lireNote(feuille.moyenne) : null;
                return (
                  <li key={m.matiere_id} className={`grid gap-x-3 gap-y-2 px-4 py-3 text-[13px] sm:grid-cols-2 xl:items-center ${COLONNES}`}>
                    <div className="flex min-w-0 items-center justify-between gap-2 sm:col-span-2 xl:col-span-1 xl:block">
                      <p className="truncate font-semibold text-gray-900">{m.nom || 'Matière'}</p>
                      <span className="xl:hidden"><BadgeStatutFeuille statut={statut} libre /></span>
                    </div>
                    <Cellule libelle="Coef.">
                      <span className={coef.hors ? 'text-amber-700' : 'text-gray-700'}>{coef.texte}</span>
                    </Cellule>
                    <div className="hidden xl:block"><BadgeStatutFeuille statut={statut} /></div>
                    <Cellule libelle="Notés">
                      <span className="tabular-nums text-gray-800">{notesSaisies(m, nbEleves)}</span>
                    </Cellule>
                    <Cellule libelle="Moyenne">
                      <span className="font-semibold tabular-nums text-gray-900">{moyenne === null ? '-' : ecrireNote(moyenne)}</span>
                    </Cellule>
                    <Cellule libelle="Au carnet" className="sm:col-span-2 xl:col-span-1">
                      <span className={carnet.alerte ? 'text-amber-700' : 'text-gray-500'}>{carnet.texte}</span>
                    </Cellule>
                    <div className="sm:col-span-2 xl:col-span-1">
                      {feuille?.id ? (
                        <Link
                          to={`/administration/notes/saisies/${feuille.id}`}
                          state={{ depuis: 'mois' }}
                          className={LIEN_LIGNE}
                        >
                          {statut === 'brouillon' ? 'Voir' : 'Voir la feuille'}
                        </Link>
                      ) : (
                        <span className="hidden text-gray-400 xl:inline">-</span>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>

      {/* Ce que fait la validation, et ce qui reste à faire ensuite */}
      <div className="flex gap-3 rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-4 py-3 text-[13px] leading-relaxed text-gray-700">
        <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#246990]" aria-hidden="true" />
        <p>
          {aValider.length > 0 ? (
            <>
              « Valider le mois » reporte d'un coup {aValider.length > 1 ? `les ${aValider.length} matières soumises` : 'la matière soumise'} dans
              le carnet, <span className="font-semibold">sans verrou</span>.
              {prevu.remplacements.map((r) => (
                <React.Fragment key={r.matiere}>
                  {' '}{pluriel(r.nombre, 'note', 'notes')} {de(r.matiere)}
                  {r.rectification
                    ? `, soumise${r.nombre > 1 ? 's' : ''} de nouveau après une rectification, ${r.nombre > 1 ? 'remplaceront celles' : 'remplacera celle'} du carnet.`
                    : ` ${r.nombre > 1 ? 'saisies' : 'saisie'} par l'école ${r.nombre > 1 ? 'seront remplacées.' : 'sera remplacée.'}`}
                </React.Fragment>
              ))}
              {verrouillees.length === 1 && (
                <>
                  {' '}La note {de(verrouillees[0].matiere)} {de(nomDuConflit(verrouillees[0]))}, verrouillée, n'est pas touchée :
                  déverrouillez-la dans le carnet, puis refaites le report depuis la feuille de la matière.
                </>
              )}
              {verrouillees.length > 1 && (
                <>
                  {' '}{pluriel(verrouillees.length, 'note verrouillée', 'notes verrouillées')} au carnet ne sont pas touchées :
                  déverrouillez-les dans le carnet, puis refaites le report depuis la feuille de chaque matière.
                </>
              )}
              {autresConflits > 0 && ' Les autres conflits sont détaillés sur la feuille de chaque matière.'}
              {' '}
            </>
          ) : (
            'Les matières arrivent ici à mesure que l\'enseignant les soumet ; « Valider le mois » les reporte alors ensemble dans le carnet, sans verrou. '
          )}
          Ensuite, enregistrez le carnet du mois : c'est ce qui verrouille les notes, calcule moyenne et rang
          {parentsOuverts ? ', et publie le mois aux familles.' : '.'} Une matière se renvoie à l'enseignant depuis sa feuille, avec un motif.
        </p>
      </div>

      {/* Valider le mois */}
      <ConfirmDialog
        isOpen={dialogue}
        onClose={fermer}
        onConfirm={valider}
        isLoading={envoi}
        type="question"
        contentClassName="sm:max-w-2xl"
        title="Valider le mois ?"
        description={`${intituleMois(classe, mois)} : ${pluriel(aValider.length, 'matière soumise', 'matières soumises')}${titulaire ? `, titulaire ${titulaire}` : ''}.`}
        confirmText="Valider et reporter"
        confirmDisabled={!peutValider}
      >
        <div className="space-y-3 text-left text-sm text-gray-700">
          <p className="text-xs text-gray-600">
            <span className="font-semibold">Matières : </span>
            {aValider.map((m) => m.nom).join(', ') || '-'}
          </p>
          <ul className="space-y-1.5">
            <li>
              {pluriel(prevu.inserees, 'note ajoutée', 'notes ajoutées')} au carnet, pour {nomMois}.
            </li>
            {prevu.remplacees > 0 && (
              <li className="font-semibold text-amber-800">
                {pluriel(prevu.remplacees, 'note du carnet, non verrouillée, sera remplacée', 'notes du carnet, non verrouillées, seront remplacées')} par
                la saisie de l'enseignant.
              </li>
            )}
            {prevu.retirees > 0 && (
              <li className="font-semibold text-amber-800">
                {pluriel(prevu.retirees, 'note du carnet sera effacée', 'notes du carnet seront effacées')} : reportée
                {prevu.retirees > 1 ? 's' : ''} par ces feuilles, la saisie ne {prevu.retirees > 1 ? 'les' : 'la'} porte plus
                (élève absent ou case vide).
              </li>
            )}
            {prevu.identiques > 0 && (
              <li>{pluriel(prevu.identiques, 'note déjà au carnet', 'notes déjà au carnet')}, sans changement.</li>
            )}
            {prevu.absents > 0 && (
              <li>{pluriel(prevu.absents, 'élève absent', 'élèves absents')} : rien n'est écrit au carnet pour {prevu.absents > 1 ? 'eux' : 'lui'}.</li>
            )}
            {prevu.conflits.length > 0 && (
              <li className="font-semibold text-red-700">
                {pluriel(prevu.conflits.length, 'élève en conflit', 'élèves en conflit')} avec le carnet : rien n'est écrit pour
                {prevu.conflits.length > 1 ? ' eux' : ' lui'}, la note du carnet reste.
              </li>
            )}
          </ul>
          {laisseesDeCote.length > 0 && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
              {laisseesDeCote.map((m) => m.nom).join(', ')} : saisie{laisseesDeCote.length > 1 ? 's' : ''} ou soumise
              {laisseesDeCote.length > 1 ? 's' : ''} par vous, {laisseesDeCote.length > 1 ? 'elles ne sont pas validées' : "elle n'est pas validée"} ici ;
              un autre membre de la direction s'en chargera.
            </p>
          )}
          {pasEncore.length > 0 && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
              {pasEncore.map((m) => `${m.nom} (${MOTS_ETAT[statutDe(m)]})`).join(', ')} : pas encore
              {pasEncore.length > 1 ? ' soumises, elles viendront' : ' soumise, elle viendra'} au carnet à
              {pasEncore.length > 1 ? ' leur' : ' sa'} propre validation.
            </p>
          )}
          <p className="rounded-md border border-[#246990]/20 bg-[#eef7fb] p-3 text-xs leading-relaxed">
            Les notes arrivent au carnet non verrouillées. La moyenne du mois, le rang et les appréciations ne sont
            calculés qu'à l'enregistrement du carnet par le secrétariat, qui verrouille alors ces notes
            {parentsOuverts ? ' et publie le mois aux familles.' : '.'}
          </p>
          {depublication && (
            <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900">
              Espace famille : ce mois est déjà publié ; il sera retiré
              {depublies ? ` à ${pluriel(depublies.length, 'famille', 'familles')}` : ' aux familles concernées'} jusqu'au
              prochain enregistrement du carnet.
            </p>
          )}
          {erreurDialogue && <p role="alert" className="text-sm text-red-700">{erreurDialogue}</p>}
        </div>
      </ConfirmDialog>

      {toast && (
        <ToastPortal>
          {shouldUseLongToast(toast.message, toast.type, toast) ? (
            <LongToast show message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          ) : (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </ToastPortal>
      )}
    </div>
  );
};

export default PageMoisPrimaire;
